import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, MessageCircle, MapPin, Check } from 'lucide-react';
import { useStore } from '@/contexts/StoreContext';
import axios from 'axios';

const API = window.location.origin + '/api';

const STORE_BACKGROUNDS = {
  'eliseo': 'https://images.unsplash.com/photo-1552825896-8059df63a1fb?w=1200&h=300&fit=crop',
  'stop-and-shop': 'https://images.unsplash.com/photo-1760986788194-e22813c56a11?w=1200&h=300&fit=crop',
  'foodworld': 'https://images.unsplash.com/photo-1574661062600-4a3cd81d2375?w=1200&h=300&fit=crop',
  'rh-hypermarket': 'https://images.unsplash.com/photo-1693505628207-dbeb3d882c92?w=1200&h=300&fit=crop',
};

export default function StorePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { setSelectedStore, selectedBranch, setSelectedBranch } = useStore();
  const [store, setStore] = useState(null);
  const [categories, setCategories] = useState([]);
  const [waLink, setWaLink] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [storeRes, catRes, waRes] = await Promise.all([
          axios.get(`${API}/supermarkets/${slug}`),
          axios.get(`${API}/categories`),
          axios.get(`${API}/whatsapp/order-link`)
        ]);
        setStore(storeRes.data);
        setCategories(catRes.data);
        if (waRes.data.configured) setWaLink(waRes.data.link);
        // Set selected store in context
        setSelectedStore(storeRes.data);
      } catch {
        navigate('/');
      }
      setLoading(false);
    };
    fetchData();
  }, [slug, navigate, setSelectedStore]);

  if (loading) return <div className="loading-screen">Loading...</div>;
  if (!store) return null;

  return (
    <div data-testid="store-page">
      {/* Store Header */}
      <div className="store-page-header" data-testid="store-header">
        <div className="store-bg-image" style={{ backgroundImage: `url(${STORE_BACKGROUNDS[slug] || STORE_BACKGROUNDS['eliseo']})` }} />
        <div className="store-header-inner">
          <button className="back-btn" onClick={() => navigate('/')} data-testid="back-home-btn" style={{ marginBottom: '0', color: 'rgba(255,255,255,0.8)' }}>
            <ArrowLeft /> All Stores
          </button>
          <div className="store-page-hero">
            <div className="store-page-logo" data-testid="store-logo">
              {store.logo_url ? (
                <img src={store.logo_url} alt={store.name} />
              ) : (
                <div style={{ width: '100%', height: '100%', background: store.color, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 16 }}>
                  <MapPin size={32} color="white" />
                </div>
              )}
            </div>
            <div className="store-page-info">
              <h1 data-testid="store-name">{store.name}</h1>
              {store.description && <p className="store-page-desc">{store.description}</p>}
            </div>
          </div>
        </div>
      </div>

      {/* Branch Picker */}
      {store.branches?.length > 0 && (
        <div className="branch-picker-section" data-testid="branch-picker">
          <div className="branch-picker-header">
            <MapPin size={16} />
            <span>{selectedBranch ? 'Your selected branch' : 'Choose your nearest branch'}</span>
          </div>
          <div className="branch-chip-grid">
            {store.branches.filter(b => b.active !== false).map(branch => {
              const isActive = selectedBranch?.id === branch.id;
              return (
                <button
                  key={branch.id}
                  type="button"
                  className={`branch-chip ${isActive ? 'branch-chip-active' : ''}`}
                  onClick={() => setSelectedBranch(branch)}
                  data-testid={`branch-chip-${branch.id}`}
                >
                  {isActive && <Check size={13} />}
                  <span>{branch.name}</span>
                </button>
              );
            })}
          </div>
          {!selectedBranch && (
            <div className="branch-picker-hint" data-testid="branch-picker-hint">
              Pick a branch to start shopping — we'll source your order from the nearest centre.
            </div>
          )}
        </div>
      )}

      {/* Categories Grid */}
      <div className="store-categories-section">
        <h2 className="section-heading" style={{ fontSize: '1.05rem', marginBottom: '14px' }}>Shop by Category</h2>
        <div className="category-grid">
          {categories.filter(c => c.slug !== 'discounts').map(cat => (
            <Link key={cat.slug} to={`/category/${cat.slug}`} className="category-btn" data-testid={`cat-btn-${cat.slug}`}>
              {cat.name}
            </Link>
          ))}
          {categories.find(c => c.slug === 'discounts') && (
            <Link to="/category/discounts" className="category-btn" style={{ background: 'var(--navy)' }} data-testid="cat-btn-discounts">
              Discounts
            </Link>
          )}
        </div>
      </div>

      {/* WhatsApp Order Link */}
      {waLink && (
        <div style={{ textAlign: 'center', padding: '8px 16px 36px' }}>
          <a
            href={`${waLink}?text=${encodeURIComponent(`Hi StoreRuns! I'd like to order from ${store.name}. Here is my list:\n\n1. \n2. \n3. \n\nPlease let me know the total and delivery time.`)}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="whatsapp-store-btn"
            className="whatsapp-home-link"
          >
            <MessageCircle size={22} /> Order via WhatsApp
          </a>
        </div>
      )}
    </div>
  );
}
